import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import HomeIconSvg from '../../assets/icons/icon-home-dashboard-tab-home.svg?react';
import IdCardIconSvg from '../../assets/icons/icon-home-dashboard-tab-page.svg?react';

import { BaseButton } from './base-primitives.styles';

type HomeDashboardBottomNavProps = {
  activeTab: 'home' | 'my-page' | null;
  onOpenHome: () => void;
  onOpenMyPage?: () => void;
};

const Nav = styled.nav.attrs({
  'data-ui': 'home-dashboard-bottom-nav',
  'aria-label': 'home tabs',
})`
  width: ${({ theme }) => theme.scale.px(291)};
  min-height: ${({ theme }) => theme.scale.px(67)};
  margin-inline: auto;
  border-radius: ${({ theme }) => theme.scale.px(15)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  padding: ${({ theme }) => theme.scale.px(7)} 0;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(8)};
`;

const TabButton = styled(BaseButton)<{ $isActive?: boolean }>`
  width: ${({ theme }) => theme.scale.px(70)};
  min-height: ${({ theme }) => theme.scale.px(53)};
  border-radius: ${({ theme }) => theme.scale.px(15)};
  display: grid;
  justify-items: center;
  align-content: center;
  gap: ${({ theme }) => theme.scale.px(4)};
  background: ${({ $isActive, theme }) =>
    $isActive ? '#484848' : 'transparent'};
  color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.surface.base : theme.colors.text.primary};

  &:disabled {
    opacity: 0.4;
  }
`;

const TabLabel = styled.span`
  font-size: ${({ theme }) => theme.scale.px(9)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(11)};
`;

const HomeIcon = styled(HomeIconSvg).attrs({ 'aria-hidden': true })`
  width: ${({ theme }) => theme.scale.px(20)};
  height: ${({ theme }) => theme.scale.px(20)};
  --fill-0: currentColor;
`;

const IdCardIcon = styled(IdCardIconSvg).attrs({ 'aria-hidden': true })`
  width: ${({ theme }) => theme.scale.px(27)};
  height: ${({ theme }) => theme.scale.px(20)};
  --fill-0: currentColor;
`;

export function HomeDashboardBottomNav({
  activeTab,
  onOpenHome,
  onOpenMyPage,
}: HomeDashboardBottomNavProps) {
  const { t } = useTranslation();
  const isMyPageTabDisabled = !onOpenMyPage;

  return (
    <Nav>
      <TabButton
        type="button"
        $isActive={activeTab === 'home'}
        aria-current={activeTab === 'home' ? 'page' : undefined}
        onClick={onOpenHome}
      >
        <HomeIcon />
        <TabLabel>{t('homeDashboard.tabHome')}</TabLabel>
      </TabButton>

      <TabButton
        type="button"
        $isActive={activeTab === 'my-page'}
        aria-current={activeTab === 'my-page' ? 'page' : undefined}
        onClick={onOpenMyPage}
        disabled={isMyPageTabDisabled}
      >
        <IdCardIcon />
        <TabLabel>{t('homeDashboard.tabMyPage')}</TabLabel>
      </TabButton>
    </Nav>
  );
}
